/**
 * @module ModalContext
 * @description Context for managing modal state throughout the application
 */

import React, {
  createContext, useState, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';

/**
 * @constant {React.Context} ModalContext
 * @description Context for sharing modal state and controls across components
 * @default {}
 */
export const ModalContext = createContext({});

const propTypes = {
  children: PropTypes.node.isRequired,
  context: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

const defaultProps = { context: ModalContext };

/**
 * @function ModalContextProvider
 * @description Provider component that makes modal state and controls available to child components
 * @param {Object} props - Component props
 * @param {React.Context} [props.context=ModalContext]
 *  Custom context to use instead of default ModalContext
 * @param {React.ReactNode} props.children
 *  Child components that will have access to the modal context
 * @returns {React.Element} Provider component with modal context
 *
 * @example
 * <ModalContextProvider>
 *   <App />
 * </ModalContextProvider>
 */
export default function ModalContextProvider({ context, children }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * @function openModal
   * @description Sets the modal state to open
   * @returns {void}
   */
  const openModal = useCallback(() => { setIsModalOpen(true); }, []);

  /**
   * @function closeModal
   * @description Sets the modal state to closed
   * @returns {void}
   */
  const closeModal = useCallback(() => { setIsModalOpen(false); }, []);

  /**
   * @type {Object} modalContextValue
   * @property {boolean} isModalOpen - Current open state of the modal
   * @property {Function} openModal - Function to open the modal
   * @property {Function} closeModal - Function to close the modal
   */
  const modalContextValue = useMemo(() => ({
    isModalOpen, openModal, closeModal,
  }), [isModalOpen, openModal, closeModal]);

  return (
    <context.Provider value={modalContextValue}>
      {children}
    </context.Provider>
  );
}

ModalContextProvider.propTypes = propTypes;
ModalContextProvider.defaultProps = defaultProps;
